import { useState, useEffect, useCallback } from 'react';
import { useFacility } from '../contexts/FacilityContext';
import { fetchSyncHealth } from '../services/api';
import { Skeleton } from '../components/Skeleton';
import './Admin.css';
import './SyncHealth.css';

// Sync Health (plan Step 15 — D22, D26; acceptance criteria 3 and 4).
//
// Lag, volume against the weekday baseline, the absence timer, the four
// internal-consistency jobs and open holds by reason, for the facility in the
// app's picker. Read-only; any staff user (S6-c's model).
//
// 🔴 THREE THINGS THIS PAGE MUST DO, all decided in the plan:
//
//   1. STATE THE CEILING ON THE PAGE (criterion 4). Nothing here can see an
//      encounter the source never sent. The banner says so, in words, above
//      every figure — a green page must not read as "reconciled".
//   2. SHOW "BASELINE BUILDING, N OF 28 DAYS" (criterion 3), never a blank or a
//      zero. The first month's silence on volume is deliberate; the page makes
//      it a visible STATE so nobody reads it as a broken panel.
//   3. NEVER SHOW A FILING DATE IT CANNOT DERIVE (R8). A hold's most urgent
//      filing state is either a payer's real deadline or days since service —
//      the server sends no date in the second case, and none is invented here.

export const CEILING_TEXT =
  'This page can only see what the source systems sent. An encounter that was never sent ' +
  'is invisible here, and no figure below can detect it. The only complete reconciliation ' +
  'is the source team’s own nightly diff against the read-back.';

const REASON_LABELS = {
  claim_form_unresolved: 'No claim form for this payer',
  coverage_missing: 'No coverage on file',
  coverage_termed: 'Coverage terminated',
  missing_prior_auth: 'Prior authorization missing',
  unknown_payer: 'Payer not recognized',
  missing_required_field: 'Missing required information',
  possible_existing_claim: 'May already be claimed',
  price_mismatch: 'Price does not match the fee schedule',
  revision_review: 'Revised by the source',
};

const DRIFT_LABELS = {
  amount_mismatch: 'Credit amount differs from the derived credit',
  dangling_pointer: 'Claim points at a row that is not its open credit',
  unpointed_row: 'Open credit the claim does not point at',
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const fmtMinutes = (m) => (m === null || m === undefined ? '—' : `${m} min`);
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : 'Never');
const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString();
};
const errorText = (err, fallback) =>
  err.response?.data?.error?.message || err.response?.data?.error || fallback;

/** The baseline panel's headline — a state in words, never a blank (criterion 3). */
export function baselineLabel(baseline) {
  if (!baseline) return 'No baseline';
  if (baseline.state === 'ready') return 'Baseline ready';
  if (baseline.state === 'building') {
    return `Baseline building, ${Math.min(baseline.history_days, baseline.required_days)} of ${baseline.required_days} days`;
  }
  return `History complete (${baseline.history_days} days) — baseline computes tonight`;
}

/** Status pill: a word AND a tone. Color never carries the state alone. */
function Status({ tone, children }) {
  const glyph = tone === 'alert' ? '!' : tone === 'quiet' ? '–' : '✓';
  return (
    <span className={`sh-status sh-status--${tone}`}>
      <span className="sh-status-glyph" aria-hidden="true">{glyph}</span>
      {children}
    </span>
  );
}

function Tile({ label, value, detail, status }) {
  return (
    <section className="sh-tile" aria-label={label}>
      <header className="sh-tile-head">
        <h2>{label}</h2>
        {status}
      </header>
      <p className="sh-tile-value">{value}</p>
      <p className="sh-tile-detail">{detail}</p>
    </section>
  );
}

function LagTile({ lag, alertsEnabled }) {
  const { current, previous, slo_minutes: slo } = lag;
  let status = <Status tone="ok">Within SLO</Status>;
  if (current.samples === 0) status = <Status tone="quiet">No data</Status>;
  else if (lag.breached && alertsEnabled) status = <Status tone="alert">Alerting</Status>;
  else if (current.breached) status = <Status tone="warn">Over SLO</Status>;
  return (
    <Tile
      label="Sync lag (p95, last 24 h)"
      value={fmtMinutes(current.p95_minutes)}
      status={status}
      detail={
        `SLO ${slo} min · ${plural(current.samples, 'record')} · previous 24 h ${fmtMinutes(previous.p95_minutes)}. ` +
        'Alerts only when two consecutive windows are over.'
      }
    />
  );
}

/** S15-k — the configured schedule, in words; never left for the reader to infer. */
export function closedDaysText(closed) {
  return closed && closed.length > 0
    ? `Closed ${closed.join(', ')}: silence those days never counts.`
    : 'No closed days configured: every day counts until a baseline exists.';
}

/** ⟳ 2026-10-21 — the next closed dates (holidays), so a calendar that has run out is visible. */
export function upcomingClosedText(upcoming) {
  if (!upcoming || upcoming.length === 0) return '';
  const list = upcoming.map((d) => `${WEEKDAYS[new Date(`${d.date}T00:00:00Z`).getUTCDay()]} ${fmtDate(d.date)} (${d.reason})`);
  return ` Next closed ${upcoming.length === 1 ? 'date' : 'dates'}: ${list.join(', ')}.`;
}

function AbsenceTile({ absence, alertsEnabled, closedWeekdays, closedDatesUpcoming }) {
  const status = absence.alerting
    ? <Status tone={alertsEnabled ? 'alert' : 'warn'}>{alertsEnabled ? 'Alerting' : 'Silent'}</Status>
    : <Status tone="ok">Receiving</Status>;
  return (
    <Tile
      label="Last received"
      value={fmtDateTime(absence.last_received_at)}
      status={status}
      detail={
        `${absence.silent_hours} h of counted silence · alerts after ${absence.threshold_hours} h. ` +
        closedDaysText(closedWeekdays) + upcomingClosedText(closedDatesUpcoming)
      }
    />
  );
}

// ⟳ 2026-10-21 — `volume_alerts_enabled === false` is a facility whose volume is
// too low for a weekday baseline to tell a normal day from a bad one (Facility B).
// Absent (an older backend) reads as on.
function BaselineTile({ baseline, volumeAlertsEnabled }) {
  const ready = baseline.state === 'ready';
  let status = ready ? <Status tone="ok">Active</Status> : <Status tone="quiet">Not alerting yet</Status>;
  let detail = ready
    ? `Computed ${fmtDateTime(baseline.computed_at)} from the trailing 8 weeks.`
    : 'Volume alerts stay off until 28 days of history exist, on purpose. The absence timer covers this period.';
  if (!volumeAlertsEnabled) {
    status = <Status tone="quiet">Alerts off</Status>;
    detail = 'Volume alerts are switched off for this facility: a low day is shown here but never alerts. ' +
      'The absence timer still alerts.';
  }
  return <Tile label="Volume baseline" value={baselineLabel(baseline)} status={status} detail={detail} />;
}

function VolumeTable({ days, volumeAlertsEnabled }) {
  const scale = Math.max(1, ...days.map((d) => Math.max(d.count, d.expected_max ?? 0)));
  return (
    <div className="sh-card sh-table-card">
      <table className="sh-table">
        <caption className="sh-caption">Encounters received per day, against the weekday baseline</caption>
        <thead>
          <tr>
            <th scope="col">Day</th>
            <th scope="col" className="num">Received</th>
            <th scope="col" className="num">Expected</th>
            <th scope="col" className="sh-bar-col">Volume</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {days.map((day) => {
            const band = day.expected_min === null ? null : `${day.expected_min}–${day.expected_max}`;
            const tip = `${fmtDate(day.date)}: ${day.count} received${band ? `, expected ${band}` : ', no baseline'}`;
            return (
              <tr key={day.date} className={day.below ? 'is-below' : ''}>
                <td>{WEEKDAYS[day.day_of_week]} {fmtDate(day.date)}</td>
                <td className="num">{day.count}</td>
                <td className="num">{band ?? '—'}</td>
                <td className="sh-bar-col">
                  <div className="sh-bar-track" title={tip}>
                    {day.expected_min !== null && (
                      <span
                        className="sh-bar-band"
                        style={{
                          left: `${(day.expected_min / scale) * 100}%`,
                          width: `${Math.max(1, ((day.expected_max - day.expected_min) / scale) * 100)}%`,
                        }}
                      />
                    )}
                    <span className="sh-bar" style={{ width: `${(day.count / scale) * 100}%` }} />
                  </div>
                </td>
                <td>
                  {day.closed ? <Status tone="quiet">{day.closed_reason ? `Closed: ${day.closed_reason}` : 'Closed'}</Status>
                    : day.partial ? <Status tone="quiet">So far today</Status>
                    : day.below ? <Status tone={volumeAlertsEnabled ? 'alert' : 'quiet'}>Below baseline</Status>
                      : day.expected_min === null ? <Status tone="quiet">No baseline</Status>
                        : <Status tone="ok">Normal</Status>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** R8: a real deadline, or days since service — never a fabricated date. */
function FilingText({ filing }) {
  if (!filing || filing.state === 'unknown') return <span className="sh-muted">No service date</span>;
  if (filing.state === 'deadline') {
    const overdue = filing.days_remaining < 0;
    return (
      <span className={filing.escalated ? 'sh-urgent' : ''}>
        {fmtDate(filing.deadline_date)} ·{' '}
        {overdue ? `${plural(Math.abs(filing.days_remaining), 'day')} past` : `${plural(filing.days_remaining, 'day')} left`}
        {' · '}{filing.payer_name}
      </span>
    );
  }
  return (
    <span className={filing.escalated ? 'sh-urgent' : ''}>
      {plural(filing.days_elapsed, 'day')} since service · no filing limit on file
    </span>
  );
}

function HoldsTable({ holds }) {
  if (holds.total === 0) {
    return <div className="sh-card sh-empty-card">Nothing is held at this facility.</div>;
  }
  return (
    <div className="sh-card sh-table-card">
      <table className="sh-table">
        <caption className="sh-caption">Open holds by reason — {plural(holds.total, 'hold')}</caption>
        <thead>
          <tr>
            <th scope="col">Reason</th>
            <th scope="col">Open</th>
            <th scope="col">Aging</th>
            <th scope="col">Oldest</th>
            <th scope="col">Most urgent filing</th>
          </tr>
        </thead>
        <tbody>
          {holds.reasons.map((group) => (
            <tr key={group.reason}>
              <td>{REASON_LABELS[group.reason] || group.reason}</td>
              <td>{group.count}</td>
              <td>{group.escalated > 0 ? <Status tone="alert">{group.escalated} aging</Status> : '—'}</td>
              <td>{group.oldest_age_days === null ? '—' : plural(group.oldest_age_days, 'day')}</td>
              <td><FilingText filing={group.most_urgent} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobCard({ title, count, tone, children }) {
  return (
    <section className="sh-card sh-job" aria-label={title}>
      <header className="sh-job-head">
        <h3>{title}</h3>
        <Status tone={tone}>{count === 0 ? 'Clear' : `${count} found`}</Status>
      </header>
      <div className="sh-job-body">{children}</div>
    </section>
  );
}

function ConsistencyJobs({ reconciliation }) {
  const { stale_intake: intake, uncoded_ready: uncoded, staged_coverage: coverage, patient_credit: credit } = reconciliation;
  return (
    <div className="sh-jobs">
      <JobCard title="Unapplied sync payments" count={intake.stale} tone={intake.stale > 0 ? 'warn' : 'ok'}>
        <p>
          {plural(intake.stale, 'allocation')} unapplied for more than {plural(intake.stale_days, 'day')}
          {intake.stale > 0 && ` ($${intake.stale_amount})`}, of {intake.unapplied} unapplied in all.
          {intake.oldest_age_days !== null && ` Oldest: ${plural(intake.oldest_age_days, 'day')}.`}
        </p>
        <p className="sh-muted">Worked on the Payments page’s unapplied-cash panel.</p>
      </JobCard>

      <JobCard title="Ready, not yet coded" count={uncoded.count} tone={uncoded.escalated > 0 ? 'alert' : uncoded.count > 0 ? 'warn' : 'ok'}>
        <p>
          {plural(uncoded.count, 'synced claim')} ready with no coder activity
          {uncoded.escalated > 0 && `, ${uncoded.escalated} near or past their filing limit`}.
        </p>
        {uncoded.items.length > 0 && (
          <ul className="sh-list">
            {uncoded.items.slice(0, 5).map((item) => (
              <li key={item.claim_id}>
                <span className="sh-mono">{item.claim_number}</span> — <FilingText filing={item.filing} />
              </li>
            ))}
          </ul>
        )}
      </JobCard>

      <JobCard title="Coverage waiting on a patient" count={coverage.staged} tone={coverage.staged > 0 ? 'warn' : 'ok'}>
        <p>
          {plural(coverage.staged, 'coverage record')} received for a source patient not yet resolved here
          {coverage.oldest_age_days !== null && `; oldest ${plural(coverage.oldest_age_days, 'day')}`}.
        </p>
        <p className="sh-muted">Applied automatically when that patient’s next encounter links them.</p>
      </JobCard>

      <JobCard
        title="Patient credit consistency"
        count={credit.enabled ? credit.drift_count : 0}
        tone={!credit.enabled ? 'quiet' : credit.drift_count > 0 ? 'alert' : 'ok'}
      >
        {!credit.enabled ? (
          <p>Patient credit balances are switched off, so there is nothing to check.</p>
        ) : credit.drift_count === 0 ? (
          <p>Every open patient credit matches its claim’s derived credit, and every claim points at its own.</p>
        ) : (
          <ul className="sh-list">
            {credit.items.slice(0, 5).map((item) => (
              <li key={`${item.kind}-${item.claim_id}`}>
                Claim <span className="sh-mono">{item.claim_id}</span> — {DRIFT_LABELS[item.kind] || item.kind}
              </li>
            ))}
          </ul>
        )}
      </JobCard>
    </div>
  );
}

function SyncHealth() {
  const { selectedFacility } = useFacility();
  const facilityId = selectedFacility?.facility_id ?? selectedFacility?.id ?? null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) { setData(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSyncHealth(facilityId);
      setData(res.data ?? null);
    } catch (err) {
      setData(null);
      setError(errorText(err, 'Failed to load sync health.'));
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="admin-page sh-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Sync Health</h1>
          <p>How late, how much, and whether anything at all is arriving from this facility’s source system.</p>
        </div>
        {facilityId && (
          <button type="button" className="sh-refresh" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </div>

      {/* Criterion 4 — the ceiling, on the page itself, above every figure. */}
      <aside className="sh-ceiling" role="note">
        <strong>What this page cannot see.</strong> {CEILING_TEXT}
      </aside>

      {error && (
        <div className="admin-modal-alert error" style={{ marginBottom: 20 }}>
          <span>{error}</span>
        </div>
      )}

      {!facilityId ? (
        <div className="admin-empty">
          <h3>Pick a facility</h3>
          <p>Sync health is shown per facility. Choose one from the facility picker above.</p>
        </div>
      ) : loading ? (
        <div className="sh-tiles" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div className="sh-tile" key={i}>
              <Skeleton className="skeleton-text" style={{ height: 14, width: 160 }} />
              <Skeleton style={{ height: 28, width: 120, marginTop: 16 }} />
              <Skeleton className="skeleton-text" style={{ height: 12, width: '80%', marginTop: 14 }} />
            </div>
          ))}
        </div>
      ) : !data ? null : !data.configured ? (
        <div className="admin-empty">
          <h3>This facility does not sync</h3>
          <p>It has no sync configuration, so no source system sends it encounters.</p>
        </div>
      ) : (
        <>
          {(!data.alerts_enabled) && (
            <p className="sh-notice">
              {data.is_sandbox ? 'Sandbox facility' : 'Sync is not enabled for this facility'} — nothing here raises an alert.
            </p>
          )}

          <div className="sh-tiles">
            <LagTile lag={data.lag} alertsEnabled={data.alerts_enabled} />
            <AbsenceTile
              absence={data.absence}
              alertsEnabled={data.alerts_enabled}
              closedWeekdays={data.closed_weekdays}
              closedDatesUpcoming={data.closed_dates_upcoming}
            />
            <BaselineTile baseline={data.volume.baseline} volumeAlertsEnabled={data.volume_alerts_enabled !== false} />
          </div>

          <h2 className="sh-section">Volume</h2>
          <VolumeTable days={data.volume.days} volumeAlertsEnabled={data.volume_alerts_enabled !== false} />

          <h2 className="sh-section">Open holds</h2>
          <HoldsTable holds={data.reconciliation.open_holds} />

          <h2 className="sh-section">Consistency checks</h2>
          <ConsistencyJobs reconciliation={data.reconciliation} />

          <p className="sh-footnote">
            Days are counted in {data.business_timezone}. Evaluated {fmtDateTime(data.evaluated_at)}.
          </p>
        </>
      )}
    </div>
  );
}

export default SyncHealth;
